import React, { FC, useContext } from "react";
import { cloneDeep } from "lodash";
import { BenchmarkingContext, BenchmarkingContextProvider, BenchmarkingDashboardDefaultView } from "@app/overviews/network-benchmarking-dashboard";
import { BenchmarkingFiltersContext, BenchmarkingFiltersContextProvider } from "@app/overviews/network-benchmarking-dashboard/filters";
import { MedicareFiltersContextProvider } from "@app/overviews/network-benchmarking-dashboard/medicare/filters";
import { NetworkComparisonFiltersContext, NetworkComparisonFiltersContextProvider } from "@app/overviews/network-benchmarking-dashboard/filters/network-comparison-filters";
import { NetworkComparisonFiltersPayload } from "@app/overviews/network-benchmarking-dashboard/filters/network-comparison-filters/shared";
import { BenchmarkingLineOfBusinessEnum } from "@app/overviews/network-benchmarking-dashboard/filters/shared";
import { QHPFiltersContextProvider } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters";
import { Page, PageInner, PageSection } from "@app/shared";
import { BenchmarkingDashboardDetailsView } from "./dashboard-details";


export const WrappedBenchmarkingDashboard: React.FC<React.PropsWithChildren<unknown>> = () => {
  const benchmarkingContext = useContext(BenchmarkingContext);
  const benchmarkingFiltersContext = useContext(BenchmarkingFiltersContext);
  const networkComparisonFiltersContext = useContext(NetworkComparisonFiltersContext);

  const onBackToFullViewClick = (): void => {
    // in case the user toggled filters without hitting apply, proceeded to click into the dashboard details view, and then want to go back to the dashboard,
    // reset all filters to reflect the actual data (not what was toggled and not actually applied)
    const originalFilters: NetworkComparisonFiltersPayload = cloneDeep(networkComparisonFiltersContext.originalDisplayedNetworkComparisonFilters);
    networkComparisonFiltersContext.setDisplayedNetworkComparisonFilters(originalFilters);
    networkComparisonFiltersContext.setSelectedNetwork(originalFilters.primaryNetwork);
    networkComparisonFiltersContext.setSelectedSpecialty(originalFilters.specialtyGroupId);
    networkComparisonFiltersContext.setSelectedPcaLineOfBusiness(originalFilters.pcaLineOfBusiness);
    networkComparisonFiltersContext.setSelectedVolumeIndicators(cloneDeep(networkComparisonFiltersContext.mappedVolumeIndicators).filter(x => originalFilters.volumeIndicators.includes(x.key)));
    networkComparisonFiltersContext.setSelectedEffectivenessValues(cloneDeep(networkComparisonFiltersContext.mappedEffectivenessAndEfficiencyValues).filter(x => originalFilters.effectivenessRanks.includes(x.key)));
    networkComparisonFiltersContext.setSelectedEfficiencyValues(cloneDeep(networkComparisonFiltersContext.mappedEffectivenessAndEfficiencyValues).filter(x => originalFilters.efficiencyRanks.includes(x.key)));
    benchmarkingContext.setShowDashboardDetails(false);
  }

  const getFiltersContextByLineOfBusiness = () => {
    const benchmarkingChildren = (
      <PageSection>
        {benchmarkingContext.showDashboardDetails ? (
          <BenchmarkingDashboardDetailsView
            filtersPayload={networkComparisonFiltersContext.displayedNetworkComparisonFilters}
            onBackToFullViewClick={onBackToFullViewClick}
            totalCount={benchmarkingContext.dashboardDetailsTotalCount}
          />
        ) : (
          <BenchmarkingDashboardDefaultView />
        )}
      </PageSection>
  );

    switch (benchmarkingFiltersContext.selectedLineOfBusiness) {
      case BenchmarkingLineOfBusinessEnum.Medicare:
        return (
          <MedicareFiltersContextProvider>
            {benchmarkingChildren}
          </MedicareFiltersContextProvider>
        );
      case BenchmarkingLineOfBusinessEnum.Marketplace:
        return (
          <QHPFiltersContextProvider>
            {benchmarkingChildren}
          </QHPFiltersContextProvider>
        );
      default:
        return (
          <MedicareFiltersContextProvider>
            {benchmarkingChildren}
          </MedicareFiltersContextProvider>
        );
    }
  } 

  return (
    <Page className="bg-white benchmarking-dashboard__page" documentTitle={`Competitive Insights Dashboard${benchmarkingContext.showDashboardDetails && " Details"}`}>
      <PageInner>
        {getFiltersContextByLineOfBusiness()}
      </PageInner>
    </Page>
  );
}

const BenchmarkingDashboard: FC = () => (
  <BenchmarkingFiltersContextProvider>
    <NetworkComparisonFiltersContextProvider>
      <BenchmarkingContextProvider>
        <WrappedBenchmarkingDashboard />
      </BenchmarkingContextProvider>
    </NetworkComparisonFiltersContextProvider>
  </BenchmarkingFiltersContextProvider>
);

export default BenchmarkingDashboard;
